import { getMonth, getYear, subMonths } from 'date-fns'
import range from 'lodash/range'
import { DateTime } from 'luxon'
import { forwardRef, useEffect, useState } from 'react'
import ReactDatePicker from 'react-datepicker'

import 'react-datepicker/dist/react-datepicker.css'
import ButtonDownPrimary from '../../../src/assets/images/icons/primary-chevron-down.svg?react'

const months = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
]

export default function DateRangePicker({
  onChange,
  onSelect,
  label,
  excludePast,
  minDate = subMonths(new Date(), 2),
  className = '',
  inline = false,
  selected = null,
  value,
  disabled,
  maxPick = new Date(),
  filter,
  ...props
}: any) {
  const [date, setDate] = useState<Date | null>(null)
  const [startDate, setStartDate] = useState<Date | null>(null)
  const [endDate, setEndDate] = useState<Date | null>(null)

  const currentDate = new Date()
  const minYear = currentDate.setFullYear(currentDate.getFullYear() - 1)
  const years = range(
    new Date(minYear).getFullYear(),
    getYear(new Date()) + 1,
    1,
  )

  useEffect(() => {
    if (startDate && endDate) onChange([startDate, endDate])
  }, [startDate, endDate])

  useEffect(() => {
    if (filter.key !== 'custom') {
      setStartDate(null)
      setEndDate(null)
      setDate(null)
    }
  }, [filter])

  return (
    <div className={`${className}`}>
      {label && (
        <label
          htmlFor={label}
          className="text-sm text-gray-700 font-medium inline-block mb-1.5 w-full"
        >
          {label}
        </label>
      )}

      <ReactDatePicker
        key={filter.key}
        disabled={disabled}
        onChange={(dates) => {
          const [start, end] = dates
          setStartDate(start)
          setEndDate(end)
          setDate(start)
        }}
        maxDate={maxPick}
        minDate={minDate}
        selectsRange
        selected={date}
        startDate={startDate === null ? undefined : startDate}
        endDate={endDate === null ? undefined : endDate}
        showPopperArrow={false}
        customInput={<CustomInput disabled={disabled} date={date} />}
        inline={inline}
        wrapperClassName="customStyle"
        showYearDropdown
        isClearable={!!date}
        renderCustomHeader={({
          date,
          changeYear,
          changeMonth,
          decreaseMonth,
          increaseMonth,
          prevMonthButtonDisabled,
          nextMonthButtonDisabled,
        }) => (
          <div className="inline-flex justify-between w-full">
            <button onClick={decreaseMonth} disabled={prevMonthButtonDisabled}>
              {'<'}
            </button>
            <div className="inline-flex space-x-2 text-sm">
              <select
                className="text-sm focus:outline-none"
                value={getYear(date)}
                onChange={({ target: { value } }) => changeYear(value as any)}
              >
                {years.map((option) => (
                  <option key={option} value={option}>
                    {option}
                  </option>
                ))}
              </select>

              <select
                className="text-sm focus:outline-none"
                value={months[getMonth(date)]}
                onChange={({ target: { value } }) =>
                  changeMonth(months.indexOf(value))
                }
              >
                {months.map((option) => (
                  <option key={option} value={option}>
                    {option}
                  </option>
                ))}
              </select>
            </div>

            <button onClick={increaseMonth} disabled={nextMonthButtonDisabled}>
              {'>'}
            </button>
          </div>
        )}
      />
    </div>
  )
}

const CustomInput = forwardRef(
  ({ value, onClick, disabled, date }: any, ref: any) => (
    <button
      type="button"
      className={`inline-flex w-full justify-between items-center space-x-2 border border-solid border-gray-300 rounded-lg shadow-sm h-10 ${
        !date && 'pr-4'
      }`}
      onClick={onClick}
      disabled={disabled}
      ref={ref}
    >
      <span
        className={`pl-4  font-medium text-sm text-gray-700 ${
          date ? 'pr-10' : ''
        }`}
      >
        {value ? value : 'Choose custom dates'}
      </span>
      {!date && <ButtonDownPrimary />}
    </button>
  ),
)
